'use client';

import { useEffect, useState } from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { Modal } from '@depop/web-ui-kit/Modal';
import { useTranslations } from 'next-intl';

import { TurnstileWidget } from './TurnstileWidget';
import styles from './styles.module.css';
import { type TurnstileModalProps } from './types';

import { getLogger } from '@/modules/observability/logging';
import { TURNSTILE_CHALLENGE_RESULT } from '@/modules/turnstile/constants';

export function TurnstileModal({
  isOpen,
  onCloseAction,
  siteKey,
}: TurnstileModalProps) {
  const t = useTranslations('common');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const logger = getLogger();

  function dispatchChallengeResult(isPassed: boolean) {
    document.dispatchEvent(
      new CustomEvent(TURNSTILE_CHALLENGE_RESULT, { detail: { isPassed } })
    );
  }

  function handleSuccess(token: string, preClearanceObtained: boolean) {
    if (preClearanceObtained) {
      dispatchChallengeResult(true);
      return onCloseAction();
    }

    dispatchChallengeResult(false);
    setError(t('TurnstileModal.Error.VerificationFailed'));
  }

  function handleError(e: unknown) {
    dispatchChallengeResult(false);
    setError(t('TurnstileModal.Error.SecurityFailed'));
    logger.error('Turnstile error:', { error: e });
  }

  // Reset error state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setIsProcessing(false);
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      setOpen={(open) => !open && onCloseAction()}
      customOverrides={{
        header: (
          <div className={styles.header}>
            <Heading variant="md">{t('TurnstileModal.Header')}</Heading>
          </div>
        ),
      }}
      content={() => (
        <div className={styles.container}>
          {!isProcessing && error && (
            <div className={styles.error}>
              <Body variant="sm-regular">{error}</Body>
            </div>
          )}
          {!error &&
            (isProcessing ? (
              <div className={styles.processing}>
                <Body variant="sm-regular">
                  {t('TurnstileModal.Processing')}
                </Body>
              </div>
            ) : (
              <div className={styles.content}>
                <Body variant="sm-regular">
                  {t('TurnstileModal.Instruction')}
                </Body>
                <TurnstileWidget
                  siteKey={siteKey}
                  onSuccessAction={handleSuccess}
                  onErrorAction={handleError}
                />
              </div>
            ))}
        </div>
      )}
    />
  );
}
